/**
 * Google Places API Service
 * 
 * This service provides an interface to Google Places API (New) for searching places,
 * retrieving place details, and handling address autocompletion. It uses the
 * @googlemaps/js-api-loader package to dynamically load the Maps JavaScript API.
 * 
 * Features:
 * - Place prediction and autocomplete functionality
 * - Detailed place information retrieval (address, coordinates, etc.)
 * - Address component parsing and structuring
 * - Singleton pattern implementation for efficient API usage
 * 
 * Usage:
 * 1. Initialize the service once at application startup
 *    initializePlacesService();
 * 
 * 2. Use the service anywhere in the application
 *    const placesService = getPlacesService();
 *    const predictions = await placesService.getPlacePredictions('123 Main St');
 * 
 * This service centralizes all Google Places API interactions to:
 * - Reduce API key exposure
 * - Prevent multiple instances and API loads
 * - Provide consistent error handling
 * - Standardize data structures across the application
 */

import { Loader } from '@googlemaps/js-api-loader';
import { GOOGLE_MAPS_CONFIG } from '../config';

/**
 * PlaceDetails interface defines the standardized place data structure
 * returned by the Places API service.
 * 
 * This structure provides a comprehensive set of place information that
 * can be used throughout the application.
 */
export class PlaceDetails {
  /**
   * Unique identifier for the place in Google's database
   */
  placeId = '';
  
  /**
   * The place name (e.g., "Hotel California")
   */
  name = '';
  
  /**
   * Complete formatted address string
   */
  formattedAddress = '';
  
  /**
   * Structured address components for flexible display and storage
   */
  addressComponents = {
    /**
     * Street number (e.g., "123")
     */
    streetNumber: '',
    
    /**
     * Street name (e.g., "Main Street")
     */
    route: '',
    
    /**
     * City/locality name (e.g., "San Francisco")
     */
    locality: '',
    
    /**
     * State/province (e.g., "California")
     */
    administrativeArea: '',
    
    /**
     * Country name (e.g., "United States")
     */
    country: '',
    
    /**
     * Postal/ZIP code (e.g., "94103")
     */
    postalCode: ''
  };
  
  /**
   * Geographic coordinates
   */
  coordinates = {
    /**
     * Latitude coordinate
     */
    lat: 0,
    
    /**
     * Longitude coordinate
     */
    lng: 0
  };
  
  /**
   * Formatted phone number (if available)
   */
  phoneNumber = '';
  
  /**
   * Website URL (if available)
   */
  website = '';

  /**
   * Google place types (e.g., "locality", "administrative_area_level_1")
   */
  types = [];
}

/**
 * PlacePrediction interface defines the structure for place suggestions
 * returned by the autocomplete service.
 */
export class PlacePrediction {
  /**
   * Unique identifier for the place
   */
  placeId = '';
  
  /**
   * Human-readable description of the place
   */
  description = '';
  
  /**
   * Place types (e.g., "address", "establishment")
   */
  types = [];
}

/**
 * PlacesService provides methods to interact with Google Places API (New)
 * including place prediction, search, and detailed information retrieval.
 *
 * Uses AutocompleteSuggestion, Place.fetchFields, and Place.searchByText
 * rather than the legacy AutocompleteService / PlacesService classes.
 */
class PlacesService {
  /**
   * Google Maps API loader instance
   * @private
   */
  loader;

  /**
   * Places library classes from google.maps.importLibrary('places')
   * @private
   */
  Place = null;
  AutocompleteSuggestion = null;
  AutocompleteSessionToken = null;

  /**
   * Billing session token for grouping autocomplete + details requests
   * @private
   */
  sessionToken = null;

  /**
   * Last autocomplete predictions, keyed by place ID, so getPlaceDetails
   * can call toPlace() and keep the session token.
   * @private
   */
  predictionByPlaceId = new Map();

  /**
   * Flag indicating if the service has been initialized
   * @private
   */
  isInitialized = false;
  
  /**
   * Flag indicating if an initialization attempt is in progress
   * @private
   */
  isInitializing = false;

  /**
   * Creates a new PlacesService instance
   * @param {string} apiKey - Google Places API key
   */
  constructor(apiKey) {
    // Validate API key presence
    if (!apiKey) {
      throw new Error('Google Places API key is required to initialize PlacesService');
    }
    
    // Create loader with required configuration
    this.loader = new Loader({
      apiKey,
      ...GOOGLE_MAPS_CONFIG.defaultOptions
    });
  }

  /**
   * Initializes the Google Places services
   * This method loads the Google Maps JavaScript API and creates service instances
   * 
   * @returns {Promise<void>} Promise that resolves when initialization is complete
   * @throws {Error} If initialization fails
   */
  async initialize() {
    // Only initialize once
    if (this.isInitialized) {
      return;
    }
    
    // Prevent multiple concurrent initialization attempts
    if (this.isInitializing) {
      console.log('PlacesService: Initialization already in progress, waiting...');
      // Wait for existing initialization to complete
      return new Promise((resolve, reject) => {
        const checkInitialized = () => {
          if (this.isInitialized) {
            resolve();
          } else if (!this.isInitializing) {
            reject(new Error('Initialization failed or was interrupted'));
          } else {
            // Check again in 100ms
            setTimeout(checkInitialized, 100);
          }
        };
        setTimeout(checkInitialized, 100);
      });
    }
    
    this.isInitializing = true;
    console.log('PlacesService: Starting initialization...');
    
    // Set up a timeout to prevent hanging on initialization
    const initTimeout = setTimeout(() => {
      if (this.isInitializing && !this.isInitialized) {
        console.error('PlacesService: Initialization timed out');
        this.isInitializing = false;
        throw new Error('Google Places API initialization timed out');
      }
    }, GOOGLE_MAPS_CONFIG.timeouts.initialization);
    
    try {
      const placesLib = await this.loadPlacesLibrary();
      this.Place = placesLib.Place;
      this.AutocompleteSuggestion = placesLib.AutocompleteSuggestion;
      this.AutocompleteSessionToken = placesLib.AutocompleteSessionToken;

      if (!this.Place || !this.AutocompleteSuggestion) {
        throw new Error('Places API (New) classes are not available after loading');
      }

      this.isInitialized = true;
      this.isInitializing = false;
      console.log('PlacesService: Initialization successful');
      clearTimeout(initTimeout);
    } catch (error) {
      clearTimeout(initTimeout);
      this.isInitializing = false;
      console.error('Failed to initialize Google Places services:', error);
      throw new Error(`Google Places API initialization failed: ${error.message}`);
    }
  }

  /**
   * Loads the Places library via the new importLibrary path.
   * @private
   */
  async loadPlacesLibrary() {
    if (typeof this.loader.importLibrary === 'function') {
      return this.loader.importLibrary('places');
    }

    await this.loader.load();
    if (!window.google?.maps?.importLibrary) {
      throw new Error('google.maps.importLibrary is not available');
    }
    return window.google.maps.importLibrary('places');
  }

  /**
   * Gets place predictions based on input text
   *
   * @param {string} input - The search text to get predictions for
   * @param {Object} options - Additional options for the prediction request
   * @param {string[]} [options.types] - Legacy place types; mapped to Places API (New) includedPrimaryTypes
   * @param {string[]} [options.countries=[]] - Country restrictions (ISO 3166-1 Alpha-2 country codes)
   * @returns {Promise<PlacePrediction[]>} Array of place predictions
   */
  async getPlacePredictions(input, options = {}) {
    await this.initialize();

    if (!input || !input.trim()) {
      return [];
    }

    if (!this.sessionToken) {
      this.sessionToken = new this.AutocompleteSessionToken();
    }

    const request = {
      input: input.trim(),
      sessionToken: this.sessionToken,
    };

    const includedPrimaryTypes = this.toIncludedPrimaryTypes(options.types);
    if (includedPrimaryTypes) {
      request.includedPrimaryTypes = includedPrimaryTypes;
    }

    if (options.countries && options.countries.length > 0) {
      request.includedRegionCodes = options.countries.map((code) => String(code).toLowerCase());
    }

    console.log('PlacesService: Sending prediction request:', JSON.stringify({
      input: request.input,
      includedPrimaryTypes: request.includedPrimaryTypes,
      includedRegionCodes: request.includedRegionCodes,
    }));

    try {
      const { suggestions } = await this.withTimeout(
        this.AutocompleteSuggestion.fetchAutocompleteSuggestions(request),
        GOOGLE_MAPS_CONFIG.timeouts.placesAutocomplete,
        'PlacesService: Prediction request timed out'
      );

      this.predictionByPlaceId.clear();
      const mappedPredictions = (suggestions || [])
        .map((suggestion) => suggestion.placePrediction)
        .filter(Boolean)
        .map((placePrediction) => {
          const result = new PlacePrediction();
          result.placeId = placePrediction.placeId || '';
          result.description = this.getPredictionText(placePrediction);
          result.types = placePrediction.types || [];
          if (result.placeId) {
            this.predictionByPlaceId.set(result.placeId, placePrediction);
          }
          return result;
        });

      console.log(`PlacesService: Got ${mappedPredictions.length} predictions`);
      return mappedPredictions;
    } catch (error) {
      console.error('PlacesService: Exception in getPlacePredictions:', error);
      if (error.message && error.message.includes('timed out')) {
        return [];
      }
      throw new Error(`Prediction failed: ${error.message}`);
    }
  }

  /**
   * Gets detailed information about a place
   *
   * @param {string} placeId - Google Place ID to get details for
   * @returns {Promise<PlaceDetails>} Detailed place information
   * @throws {Error} If the place details cannot be retrieved
   */
  async getPlaceDetails(placeId) {
    await this.initialize();

    if (!placeId) {
      throw new Error('Place ID is required to get place details');
    }

    console.log(`PlacesService: Getting place details for ID: ${placeId}`);

    const cachedPrediction = this.predictionByPlaceId.get(placeId);
    const place = cachedPrediction
      ? cachedPrediction.toPlace()
      : new this.Place({ id: placeId });

    try {
      await this.withTimeout(
        place.fetchFields({
          fields: [
            'id',
            'displayName',
            'formattedAddress',
            'addressComponents',
            'location',
            'types',
            'nationalPhoneNumber',
            'websiteURI',
          ],
        }),
        GOOGLE_MAPS_CONFIG.timeouts.placeDetails,
        'Place details request timed out'
      );

      this.sessionToken = null;
      this.predictionByPlaceId.clear();

      const details = this.mapGooglePlaceToDetails(place);
      console.log('PlacesService: Place details retrieved successfully');
      return details;
    } catch (error) {
      console.error('PlacesService: Exception in getPlaceDetails:', error);
      throw error;
    }
  }

  /**
   * Text search for places (Places API New replacement for findPlaceFromQuery).
   *
   * @param {string} query - Free-text query
   * @param {Object} [options]
   * @param {string} [options.includedType] - Optional Table A type filter
   * @param {number} [options.maxResultCount=5]
   * @returns {Promise<PlaceDetails[]>}
   */
  async searchPlacesByText(query, options = {}) {
    await this.initialize();

    if (!query || !query.trim()) {
      return [];
    }

    const request = {
      textQuery: query.trim(),
      fields: [
        'id',
        'displayName',
        'formattedAddress',
        'addressComponents',
        'location',
        'types',
      ],
      maxResultCount: options.maxResultCount || 5,
    };

    if (options.includedType) {
      request.includedType = options.includedType;
    }

    const { places } = await this.Place.searchByText(request);
    return (places || []).map((place) => this.mapGooglePlaceToDetails(place));
  }

  /**
   * Maps a Places API (New) Place object onto our PlaceDetails shape.
   * @private
   */
  mapGooglePlaceToDetails(place) {
    const details = new PlaceDetails();
    details.placeId = place.id || '';
    details.name = this.getDisplayName(place);
    details.formattedAddress = place.formattedAddress || '';
    details.phoneNumber = place.nationalPhoneNumber || '';
    details.website = place.websiteURI || '';
    details.types = place.types || [];
    details.coordinates = this.extractCoordinates(place.location);

    const components = place.addressComponents;
    if (components && components.length > 0) {
      details.addressComponents = {
        streetNumber: this.findAddressComponent(components, 'street_number'),
        route: this.findAddressComponent(components, 'route'),
        locality: this.findAddressComponent(components, 'locality')
          || this.findAddressComponent(components, 'postal_town')
          || this.findAddressComponent(components, 'sublocality')
          || this.findAddressComponent(components, 'administrative_area_level_2'),
        administrativeArea: this.findAddressComponent(components, 'administrative_area_level_1'),
        country: this.findAddressComponent(components, 'country'),
        postalCode: this.findAddressComponent(components, 'postal_code'),
      };
    }

    return details;
  }

  /**
   * Maps legacy Autocomplete types onto Places API (New) includedPrimaryTypes.
   * Empty / geocode / unrestricted → omit (all types, including states like South Australia).
   * @private
   */
  toIncludedPrimaryTypes(types) {
    if (!types || types.length === 0) {
      return undefined;
    }

    const first = types[0];
    if (!first || first === 'geocode') {
      return undefined;
    }
    if (first === '(cities)' || first === 'cities' || first === 'locality') {
      return ['locality'];
    }
    if (first === 'address') {
      return ['street_address'];
    }
    return types.slice(0, 5);
  }

  /**
   * @private
   */
  getPredictionText(placePrediction) {
    if (!placePrediction) return '';
    if (placePrediction.text?.text) return placePrediction.text.text;
    if (typeof placePrediction.text?.toString === 'function') {
      return placePrediction.text.toString();
    }
    return placePrediction.text || '';
  }

  /**
   * @private
   */
  getDisplayName(place) {
    const name = place?.displayName;
    if (!name) return '';
    if (typeof name === 'string') return name;
    return name.text || '';
  }

  /**
   * @private
   */
  extractCoordinates(location) {
    if (!location) {
      return { lat: 0, lng: 0 };
    }
    const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
    const lng = typeof location.lng === 'function' ? location.lng() : location.lng;
    return { lat: lat || 0, lng: lng || 0 };
  }

  /**
   * @private
   */
  withTimeout(promise, timeoutMs, timeoutMessage) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error(timeoutMessage));
      }, timeoutMs);

      promise.then(
        (value) => {
          clearTimeout(timer);
          resolve(value);
        },
        (error) => {
          clearTimeout(timer);
          reject(error);
        }
      );
    });
  }

  /**
   * Helper method to extract a specific address component
   *
   * @param {Array} components - Array of address components from Google Places API
   * @param {string} type - The type of component to find
   * @returns {string} The value of the component, or empty string if not found
   * @private
   */
  findAddressComponent(components, type) {
    const component = components.find((item) =>
      item.types && item.types.includes(type)
    );

    if (!component) {
      return '';
    }
    return component.longText || component.long_name || '';
  }
}

/**
 * Singleton instance of the Places service
 * This ensures only one instance exists across the application
 * @private
 */
let placesServiceInstance = null;

/**
 * Initializes the Places service with the API key from environment variables
 * This should be called once at application startup
 * 
 * @param {Object} options - Initialization options
 * @param {boolean} [options.force=false] - Force re-initialization even if already initialized
 * @param {string} [options.apiKey] - Optional API key to use instead of environment variable
 * @returns {PlacesService} The initialized Places service instance
 * @throws {Error} If the API key is missing or initialization fails
 */
export const initializePlacesService = (options = {}) => {
  const { force = false, apiKey: providedApiKey = null } = options;
  
  // If force is true, reset the instance to null to re-initialize
  if (force && placesServiceInstance) {
    console.log('Forcing re-initialization of Places service');
    placesServiceInstance = null;
  }
  
  // Only create a new instance if one doesn't exist
  if (!placesServiceInstance) {
    // Get API key from options or environment variables or config
    const apiKey = providedApiKey || 
                   process.env.REACT_APP_GOOGLE_PLACES_API_KEY || 
                   GOOGLE_MAPS_CONFIG.apiKey;
    
    // Log available environment variables for debugging (redacted for security)
    console.log('Environment variables available:', 
      Object.keys(process.env)
        .filter(key => key.startsWith('REACT_APP_'))
        .map(key => key)
    );
    
    // Validate API key presence
    if (!apiKey) {
      console.error('API Key not found in environment. Available env vars:', 
        Object.keys(process.env)
          .filter(key => key.startsWith('REACT_APP_'))
          .join(', ')
      );
      
      throw new Error(
        'Google Places API key not found. ' +
        'Please ensure REACT_APP_GOOGLE_PLACES_API_KEY is set in your .env or .env.local file.'
      );
    }
    
    // Verify API key format (basic check)
    if (typeof apiKey !== 'string' || apiKey.length < 10) {
      throw new Error(
        'Google Places API key appears to be invalid. ' +
        'Please check the format of your API key.'
      );
    }
    
    // Console log that initialization is happening (helpful for debugging)
    console.log('Initializing Google Places Service with API key starting with:', apiKey.substring(0, 4) + '...');
    
    // Create a new instance
    try {
      placesServiceInstance = new PlacesService(apiKey);
      console.log('Places service instance created successfully');
      
      // Try to initialize immediately to catch early failures
      placesServiceInstance.initialize().then(() => {
        console.log('Places service initialized successfully in background');
      }).catch(error => {
        console.error('Places service background initialization failed:', error);
      });
    } catch (error) {
      console.error('Failed to create Places service instance:', error);
      throw error;
    }
  }
  
  return placesServiceInstance;
};

/**
 * Gets the Places service instance
 * This ensures the service is initialized before use
 * 
 * @returns {PlacesService} The Places service instance
 * @throws {Error} If the service hasn't been initialized yet
 */
export const getPlacesService = () => {
  // Check if the service has been initialized
  if (!placesServiceInstance) {
    throw new Error(
      'Places service not initialized. ' +
      'Please call initializePlacesService() first.'
    );
  }
  
  return placesServiceInstance;
}; 